/**
 * CallbackPage Component
 *
 * Handles OAuth callback after Okta authentication.
 * Validates tokens, checks group membership, and redirects appropriately.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleCallback, oktaAuth } from '../auth';

const CallbackPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        console.log('Processing OAuth callback...');
        const result = await handleCallback();

        if (result.success) {
          console.log('Authentication successful, redirecting to app...');

          // Get the original URI that was saved before redirect
          const originalUri = oktaAuth.getOriginalUri();

          // Navigate to original URI or default to home
          if (originalUri && originalUri !== window.location.origin) {
            navigate(originalUri, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        } else {
          console.error('Authentication failed:', result.error);
          setError(result.error || 'Authentication failed');

          // Redirect to access denied page after a delay
          setTimeout(() => {
            navigate('/access-denied', { replace: true });
          }, 3000);
        }
      } catch (err) {
        console.error('Callback processing error:', err);
        setError(err.message || 'An unexpected error occurred');

        // Redirect to access denied page after a delay
        setTimeout(() => {
          navigate('/access-denied', { replace: true });
        }, 3000);
      }
    };

    processCallback();
  }, [navigate]);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f5f5f5',
    }}>
      <div style={{
        textAlign: 'center',
        padding: '48px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        maxWidth: '500px',
      }}>
        {error ? (
          <>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
            <h2 style={{ marginBottom: '16px', color: '#d32f2f' }}>Authentication Failed</h2>
            <p style={{ color: '#666', marginBottom: '24px' }}>{error}</p>
            <p style={{ fontSize: '14px', color: '#999' }}>Redirecting to access denied page...</p>
          </>
        ) : (
          <>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <h2 style={{ marginBottom: '16px' }}>Completing Sign In</h2>
            <p style={{ color: '#666' }}>Please wait while we verify your credentials...</p>
            <div style={{
              marginTop: '24px',
              width: '100%',
              height: '4px',
              backgroundColor: '#e0e0e0',
              borderRadius: '2px',
              overflow: 'hidden',
            }}>
              <div style={{
                width: '30%',
                height: '100%',
                backgroundColor: '#1976d2',
                animation: 'progress 1.5s ease-in-out infinite',
              }} />
            </div>
          </>
        )}
      </div>
      <style>{`
        @keyframes progress {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(300%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
};

export default CallbackPage;
